// V2 Template types — re-exported for convenience
import type {
  TemplateCategory,
  TemplateFormat,
  TemplateScope,
  TemplateStatus,
} from '@/services/api/generated/templatesV2Service';

export type { TemplateCategory, TemplateFormat, TemplateScope, TemplateStatus };

// Display names for template categories (maps v2 TemplateCategory values)
export const TEMPLATE_CATEGORY_DISPLAY_NAMES: Partial<
  Record<TemplateCategory, string>
> = {
  RETAINER: 'Engagement Letter',
  LOR_GENERIC: 'Letter of Representation',
  LOR_FIRST_PARTY: 'First Party Letter of Representation',
  LOR_THIRD_PARTY: 'Third Party Letter of Representation',
  LOR_HEALTH: 'First Party LoR - Health Insurance',
  DROP_LETTER: 'Drop Letter',
  JOINT_VENTURE_NOTICE: 'Joint Venture Notice',
  HIPAA_COVERSHEET: 'HIPAA Coversheet',
  HIPAA_AUTHORIZATION: 'HIPAA Authorization',
  FOIA_LETTER: 'FOIA Letter',
  LIEN_NOTICE: 'Lien Notice',
  WELCOME_LETTER: 'Welcome Letter',
  FIRM_AUTHORIZATION: 'Firm Authorization',
  OTHER: 'Other',
};

// Legacy enum — DEPRECATED. Use TemplateCategory from v2 instead.
// Kept temporarily for consumers that haven't migrated yet.
export enum DocumentTemplateType {
  ENGAGEMENT_LETTER = 'ENGAGEMENT_LETTER',
  LETTER_OF_REPRESENTATION = 'LETTER_OF_REPRESENTATION',
  FIRST_PARTY_LETTER_OF_REPRESENTATION = 'FIRST_PARTY_LETTER_OF_REPRESENTATION',
  THIRD_PARTY_LETTER_OF_REPRESENTATION = 'THIRD_PARTY_LETTER_OF_REPRESENTATION',
  FIRST_PARTY_LETTER_OF_REPRESENTATION_HEALTH_INSURANCE = 'FIRST_PARTY_LETTER_OF_REPRESENTATION_HEALTH_INSURANCE',
  JOINT_VENTURE_NOTICE = 'JOINT_VENTURE_NOTICE',
  DROP_LETTER = 'DROP_LETTER',
}

// Legacy display names — DEPRECATED. Use TEMPLATE_CATEGORY_DISPLAY_NAMES instead.
export const DOCUMENT_TEMPLATE_TYPE_DISPLAY_NAMES: Record<
  DocumentTemplateType,
  string
> = {
  [DocumentTemplateType.ENGAGEMENT_LETTER]: 'Engagement Letter',
  [DocumentTemplateType.LETTER_OF_REPRESENTATION]: 'Letter of Representation',
  [DocumentTemplateType.FIRST_PARTY_LETTER_OF_REPRESENTATION]:
    'First Party Letter of Representation',
  [DocumentTemplateType.THIRD_PARTY_LETTER_OF_REPRESENTATION]:
    'Third Party Letter of Representation',
  [DocumentTemplateType.FIRST_PARTY_LETTER_OF_REPRESENTATION_HEALTH_INSURANCE]:
    'First Party Letter of Representation - Health Insurance',
  [DocumentTemplateType.JOINT_VENTURE_NOTICE]: 'Joint Venture Notice',
  [DocumentTemplateType.DROP_LETTER]: 'Drop Letter',
};

/**
 * Maps legacy DocumentTemplateType values to v2 TemplateCategory.
 * Backend migration confirmed: ENGAGEMENT_LETTER → RETAINER.
 */
export const DOCUMENT_TYPE_TO_CATEGORY: Record<
  DocumentTemplateType,
  TemplateCategory
> = {
  [DocumentTemplateType.ENGAGEMENT_LETTER]: 'RETAINER',
  [DocumentTemplateType.LETTER_OF_REPRESENTATION]: 'LOR_GENERIC',
  [DocumentTemplateType.FIRST_PARTY_LETTER_OF_REPRESENTATION]: 'LOR_FIRST_PARTY',
  [DocumentTemplateType.THIRD_PARTY_LETTER_OF_REPRESENTATION]: 'LOR_THIRD_PARTY',
  [DocumentTemplateType.FIRST_PARTY_LETTER_OF_REPRESENTATION_HEALTH_INSURANCE]:
    'LOR_HEALTH',
  [DocumentTemplateType.JOINT_VENTURE_NOTICE]: 'JOINT_VENTURE_NOTICE',
  [DocumentTemplateType.DROP_LETTER]: 'DROP_LETTER',
};

// Document Field Mappings - centralized enum matching backend
export enum DocumentFieldMapping {
  ADJUSTER_NAME = 'adjuster_name',
  ATTORNEY_NAME = 'attorney_name',
  ATTORNEY_BAR_NUMBER = 'attorney_bar_number',
  ADJUSTER_CONTACT = 'adjuster_contact',
  CASE_TYPE = 'case_type',
  CLAIM_NUMBER = 'claim_number',
  CLIENT_ADDRESS = 'client_address',
  // New client address 2-line support
  CLIENT_ADDRESS_1 = 'client_address_1',
  CLIENT_ADDRESS_2 = 'client_address_2',
  CLIENT_PHONE_NUMBER = 'client_phone_number',
  CLIENT_EMAIL_ADDRESS = 'client_email_address',
  CLIENT_DATE_OF_BIRTH = 'client_date_of_birth',
  CLIENT_NAME = 'client_name',
  CLIENT_LAST_NAME = 'client_last_name',
  DATE_OF_LOSS = 'date_of_loss',
  CARRIER_ADDRESS_1 = 'carrier_address_1',
  CARRIER_ADDRESS_2 = 'carrier_address_2',
  CARRIER_NAME = 'carrier_name',
  CARRIER_TELEPHONE = 'carrier_telephone',
  CARRIER_FAX = 'carrier_fax',
  CONTACT_METHOD = 'contact_method',
  ACCIDENT_LOCATION = 'accident_location',
  INSURED_NAME = 'insured_name',
  POLICY_NUMBER = 'policy_number',
  POST_FILING_CONTINGENCY_FEE = 'post_filing_contingency_fee',
  PRE_FILING_CONTINGENCY_FEE = 'pre_filing_contingency_fee',
  RECIPIENT_NAME = 'recipient_name',
  TODAYS_DATE = 'todays_date',
  YOUR_INSURED = 'your_insured',
  // New incident/premises/adverse and meta
  CMS_FILE_NUMBER = 'cms_file_number',
  AT_FAULT_NAME = 'at_fault_name',
  AT_FAULT_ADDRESS_1 = 'at_fault_address_1',
  AT_FAULT_ADDRESS_2 = 'at_fault_address_2',
  PREMISES_NAME = 'premises_name',
  PREMISES_ADDRESS_1 = 'premises_address_1',
  PREMISES_ADDRESS_2 = 'premises_address_2',
  LOSS_TIME = 'loss_time',
  PLUS_2_HOURS_LOSS_TIME = 'plus_2_hours_loss_time',
  MINUS_2_HOURS_LOSS_TIME = 'minus_2_hours_loss_time',
  ONE_WEEK_FROM_TODAYS_DATE = 'one_week_from_todays_date',
  LOSS_FACTS = 'loss_facts',
  MANAGING_PARALEGAL_NAME = 'managing_paralegal_name',
  MANAGING_PARALEGAL_INITIALS = 'managing_paralegal_initials',
  MANAGING_PARALEGAL_NUMBER = 'managing_paralegal_number',
  MANAGING_PARALEGAL_EMAIL = 'managing_paralegal_email',
}

// Display names for field mappings
export const DOCUMENT_FIELD_MAPPING_DISPLAY_NAMES: Record<
  DocumentFieldMapping,
  string
> = {
  [DocumentFieldMapping.ADJUSTER_NAME]: 'Adjuster Name',
  [DocumentFieldMapping.ATTORNEY_NAME]: 'Attorney Name',
  [DocumentFieldMapping.ATTORNEY_BAR_NUMBER]: 'Attorney Bar Number',
  [DocumentFieldMapping.ADJUSTER_CONTACT]: 'Adjuster Contact',
  [DocumentFieldMapping.CASE_TYPE]: 'Case Type',
  [DocumentFieldMapping.CLAIM_NUMBER]: 'Claim Number',
  [DocumentFieldMapping.CLIENT_ADDRESS]: 'Client Address',
  [DocumentFieldMapping.CLIENT_ADDRESS_1]: 'Client Address 1',
  [DocumentFieldMapping.CLIENT_ADDRESS_2]: 'Client Address 2',
  [DocumentFieldMapping.CLIENT_PHONE_NUMBER]: 'Client Phone Number',
  [DocumentFieldMapping.CLIENT_EMAIL_ADDRESS]: 'Email Address',
  [DocumentFieldMapping.CLIENT_DATE_OF_BIRTH]: 'Date of Birth',
  [DocumentFieldMapping.CLIENT_NAME]: 'Client Name',
  [DocumentFieldMapping.CLIENT_LAST_NAME]: 'Client Last Name',
  [DocumentFieldMapping.DATE_OF_LOSS]: 'Date of Loss',
  [DocumentFieldMapping.CARRIER_ADDRESS_1]: 'Insurance Address',
  [DocumentFieldMapping.CARRIER_ADDRESS_2]: 'Insurance Address 2',
  [DocumentFieldMapping.CARRIER_NAME]: 'Insurance Company Name',
  [DocumentFieldMapping.CARRIER_TELEPHONE]: 'Carrier Telephone',
  [DocumentFieldMapping.CARRIER_FAX]: 'Carrier Fax',
  [DocumentFieldMapping.CONTACT_METHOD]: 'Contact Method',
  [DocumentFieldMapping.ACCIDENT_LOCATION]: 'Accident Location',
  [DocumentFieldMapping.INSURED_NAME]: 'Insured Name',
  [DocumentFieldMapping.POLICY_NUMBER]: 'Policy Number',
  [DocumentFieldMapping.POST_FILING_CONTINGENCY_FEE]:
    'Post-filing contingency fee',
  [DocumentFieldMapping.PRE_FILING_CONTINGENCY_FEE]:
    'Pre-filing contingency fee',
  [DocumentFieldMapping.RECIPIENT_NAME]: 'Recipient Name',
  [DocumentFieldMapping.TODAYS_DATE]: "Today's Date",
  [DocumentFieldMapping.YOUR_INSURED]: 'Your Insured',
  [DocumentFieldMapping.CMS_FILE_NUMBER]: 'CMS File Number',
  [DocumentFieldMapping.AT_FAULT_NAME]: 'At-Fault Name',
  [DocumentFieldMapping.AT_FAULT_ADDRESS_1]: 'At-Fault Address 1',
  [DocumentFieldMapping.AT_FAULT_ADDRESS_2]: 'At-Fault Address 2',
  [DocumentFieldMapping.PREMISES_NAME]: 'Premises Name',
  [DocumentFieldMapping.PREMISES_ADDRESS_1]: 'Premises Address 1',
  [DocumentFieldMapping.PREMISES_ADDRESS_2]: 'Premises Address 2',
  [DocumentFieldMapping.LOSS_TIME]: 'Loss Time',
  [DocumentFieldMapping.PLUS_2_HOURS_LOSS_TIME]: '+2 Hours Loss Time',
  [DocumentFieldMapping.MINUS_2_HOURS_LOSS_TIME]: '-2 Hours Loss Time',
  [DocumentFieldMapping.ONE_WEEK_FROM_TODAYS_DATE]:
    "One Week From Today's Date",
  [DocumentFieldMapping.LOSS_FACTS]: 'Loss Facts',
  [DocumentFieldMapping.MANAGING_PARALEGAL_NAME]: 'Managing Paralegal Name',
  [DocumentFieldMapping.MANAGING_PARALEGAL_INITIALS]:
    'Managing Paralegal Initials',
  [DocumentFieldMapping.MANAGING_PARALEGAL_NUMBER]: 'Managing Paralegal Phone',
  [DocumentFieldMapping.MANAGING_PARALEGAL_EMAIL]: 'Managing Paralegal Email',
};

export enum DocumentInstanceStatus {
  CREATED = 'CREATED',
  SENT = 'SENT',
  COMPLETED = 'COMPLETED',
  VOIDED = 'VOIDED',
  SIGNED = 'SIGNED',
}

export const DOCUMENT_INSTANCE_STATUS_DISPLAY_NAMES: Record<
  DocumentInstanceStatus,
  string
> = {
  [DocumentInstanceStatus.CREATED]: 'Created',
  [DocumentInstanceStatus.SENT]: 'Sent',
  [DocumentInstanceStatus.COMPLETED]: 'Completed',
  [DocumentInstanceStatus.VOIDED]: 'Voided',
  [DocumentInstanceStatus.SIGNED]: 'Signed by all parties',
};

/**
 * DocumentTemplate — matches the v2 backend `DocumentTemplateSerializer` shape.
 *
 * The backend `DocumentInstanceSerializer.get_document_template()` returns
 * v2 Template data via `DocumentTemplateSerializer` (see backend serializers/documents.py).
 * Fields use v2 names: `category` (not `template_type`), `format`, `scope`, `status`.
 */
export interface DocumentTemplate {
  id: string;
  name: string;
  display_name?: string | null;
  category: TemplateCategory;
  format: TemplateFormat;
  scope: TemplateScope;
  status: TemplateStatus;
  attorney_id?: string | null;
  attorney_name?: string | null;
  requires_signatures: boolean;
}

/**
 * Bridge type that carries both v2 and legacy field names.
 *
 * V2 field names (from ReadTemplateField):
 *   context_key, reference, field_type, required, template_id
 *
 * Legacy field names (kept for backward-compat during migration):
 *   mapping, external_reference, type, is_required, document_template
 *
 * Use `adaptTemplateFields()` to convert ReadTemplateField[] from the API
 * into this shape. Consumers should migrate to v2 names over time.
 */
export interface DocumentTemplateField {
  id: string;
  display_name?: string | null;
  default_value: string | ScalarChoice | ScalarChoice[] | null;

  // V2 field names (preferred)
  context_key?: string | null;
  reference: string;
  field_type: string;
  required: boolean;
  template_id: string;
  source?: string;

  // Legacy aliases (kept during migration — identical values to v2 names)
  mapping: DocumentFieldMapping;
  external_reference: string;
  type: string;
  is_required: boolean;
  document_template: string;
}

// Legacy wrapper — DEPRECATED. V2 returns ReadTemplateField[] directly.
export interface DocumentTemplateFields {
  fields: DocumentTemplateField[];
}

/**
 * Adapter: converts v2 ReadTemplateField[] into the bridge DocumentTemplateField[].
 *
 * Populates both v2 names and legacy aliases so existing consumers continue to
 * work while new code can use the v2 names.
 */
export const adaptTemplateFields = (
  v2Fields: ReadTemplateFieldLike[],
): DocumentTemplateField[] =>
  v2Fields
    .filter((f) => f.source !== 'SIGNING' && f.field_type !== 'IMAGE')
    .map((f) => ({
    id: f.id,
    display_name: f.display_name ?? undefined,
    default_value: f.default_value ?? null,

    // V2 names
    context_key: f.context_key ?? null,
    reference: f.reference,
    field_type: f.field_type,
    required: f.required,
    template_id: f.template_id,
    source: f.source,

    // Legacy aliases — use reference as the canonical mapping key.
    // reference matches DocumentFieldMapping enum values (e.g. 'adjuster_contact',
    // 'carrier_name'). context_key is the Matter Context field key
    // (e.g. 'insuranceAdjusterPhone') which is a different namespace.
    mapping: f.reference as DocumentFieldMapping,
    external_reference: f.reference,
    type: f.field_type,
    is_required: f.required,
    document_template: f.template_id,
  }));

/**
 * Minimal shape expected by adaptTemplateFields — matches ReadTemplateField
 * without importing from generated code (to avoid circular deps).
 */
interface ReadTemplateFieldLike {
  id: string;
  display_name?: string | null;
  default_value?: string | null;
  context_key?: string | null;
  reference: string;
  field_type: string;
  required: boolean;
  template_id: string;
  source?: string;
}

/**
 * V2-native field type for LOR display panels.
 *
 * Uses only v2 field names (no legacy aliases). The `default_value` supports
 * choice arrays derived from coverage data, unlike the generated
 * ReadTemplateField which only allows `string | null`.
 */
export interface LorDisplayField {
  id: string;
  display_name?: string | null;
  default_value: string | ScalarChoice | ScalarChoice[] | null;
  reference: string;
  field_type: string;
  required: boolean;
  template_id: string;
  context_key?: string | null;
  source?: string;
}

/**
 * Convert raw v2 ReadTemplateField[] into LorDisplayField[] with choice arrays
 * injected from a coverage choice map.
 *
 * This is the single shared utility for template field mapping — all LOR
 * consumers (CoverageLoRForm, ClaimLorStep, DraftClaimLorStep) should use this
 * instead of duplicating the filter/map logic.
 */
export const buildLorDisplayFields = (
  rawFields: Array<{
    id: string;
    display_name?: string | null;
    default_value?: string | null;
    reference: string;
    field_type: string;
    required: boolean;
    template_id: string;
    context_key?: string | null;
    source?: string;
  }>,
  choiceMap: Record<string, ScalarChoice[]>,
): { fields: LorDisplayField[] } => {
  const fields: LorDisplayField[] = rawFields
    .filter((f) => f.source !== 'SIGNING' && f.field_type !== 'IMAGE')
    .map((f) => {
      const ref = f.reference as DocumentFieldMapping;
      const choiceArray = COVERAGE_CHOICE_FIELDS.has(ref)
        ? choiceMap[ref] ?? null
        : null;
      return {
        id: f.id,
        display_name: f.display_name ?? null,
        default_value: choiceArray ?? f.default_value ?? null,
        reference: f.reference,
        field_type: f.field_type,
        required: f.required,
        template_id: f.template_id,
        context_key: f.context_key ?? null,
        source: f.source,
      };
    });
  return { fields };
};

export interface DocumentCreationRequest {
  document_template_id: string;
  merge_fields: Record<string, string>;
  coverage_id?: string;
  allow_duplicate?: boolean;
}

export interface SignerInfo {
  status: 'SENT' | 'SIGNED';
  email: string | null;
  name: string | null;
}

export interface DetailedAgreementInfo {
  external_url: string;
  signer_info: SignerInfo[];
}
// Legacy source enum — DEPRECATED. V2 uses ExternalSystem from templatesV2Service.
export enum DocumentTemplateSource {
  FINCH_TEMPLATE = 'FINCH_TEMPLATE',
  ADOBE_SIGN_TEMPLATE = 'ADOBE_SIGN_TEMPLATE',
  FINCH_TMP_DOCX_ONLY = 'DOCX_ONLY',
}

export interface DocumentInstance {
  id: string;
  status: DocumentInstanceStatus;
  external_document_reference: string;
  document_template: DocumentTemplate;
  detailed_agreement_info: Partial<DetailedAgreementInfo>;
  created_at: string;
  field_values?: Record<string, unknown> | null;
  cached_pdf_url?: string | null;
  field_values_source?: string | null; // "system" for AI/FinchGPT or user UUID
  lor_filename?: string | null;
}

export interface ScalarChoice {
  value: string;
  context?: Record<string, unknown>;
}

// Enhanced scalar choice for LoR fields with typed context
export interface LoRScalarChoice {
  value: string;
  context?: {
    coverage_id: string;
    carrier?: {
      name?: string;
      address?: string;
    };
    benefit_type?: string;
    identifier_type?: string;
    claim_identifier?: string;
    name?: string; // adjuster name
    handles?: Array<{
      handle_type: string;
      value: string;
      name?: string;
    }>;
    contact_handle?: {
      handle_type: string;
      value: string;
      name?: string;
    };
    /** Unique identifier to tie contacts to the originating adjuster */
    adjuster_id?: string;
    /** Source of contact handle: "adjuster" or "carrier" */
    contact_source?: 'adjuster' | 'carrier';
  };
}

/**
 * Fields whose default_value should be a LoRScalarChoice[] array derived from
 * coverage data (adjusters, policy_identifiers, claims, named_insured).
 */
export const COVERAGE_CHOICE_FIELDS = new Set<string>([
  DocumentFieldMapping.ADJUSTER_NAME,
  DocumentFieldMapping.ADJUSTER_CONTACT,
  DocumentFieldMapping.POLICY_NUMBER,
  DocumentFieldMapping.CLAIM_NUMBER,
  DocumentFieldMapping.INSURED_NAME,
]);

/**
 * Build a map of DocumentFieldMapping → ScalarChoice[] from coverage data.
 *
 * This is the single place that derives dropdown option lists from raw coverage
 * query results.  Every LOR consumer (CoverageLoRForm, ClaimLorStep,
 * DraftClaimLorStep) should call this instead of duplicating derivation logic.
 */
export const buildCoverageChoiceMap = (
  coverageData: {
    adjusters?: Array<{
      name?: string | null;
      benefit_type?: string | null;
      handles?: Array<{ handle_type: string; value: string; name?: string | null }> | null;
    }> | null;
    carrier?: { name?: string | null; address?: string | null; handles?: Array<{ handle_type: string; value: string; name?: string | null }> | null } | null;
    policy_identifiers?: Array<{ identifier_type?: string; identifier_value: string }> | null;
    claims?: Array<{ benefit_type?: string | null; claim_identifier?: string | null }> | null;
    named_insured?: string[] | null;
  } | undefined,
  coverageId: string,
): Record<string, ScalarChoice[]> => {
  if (!coverageData) return {};

  const result: Record<string, ScalarChoice[]> = {};

  // Adjuster name choices
  const adjusters = coverageData.adjusters ?? [];
  const adjusterNameChoices: ScalarChoice[] = adjusters
    .filter((a) => a.name)
    .map((a, idx) => ({
      value: a.name as string,
      context: {
        coverage_id: coverageId,
        carrier: coverageData.carrier ? { name: coverageData.carrier.name, address: coverageData.carrier.address } : undefined,
        benefit_type: a.benefit_type ?? undefined,
        name: a.name ?? undefined,
        adjuster_id: `adj-${idx}`,
      },
    }));
  if (adjusterNameChoices.length > 0) {
    result[DocumentFieldMapping.ADJUSTER_NAME] = adjusterNameChoices;
  }

  // Adjuster contact choices (EMAIL > FAX sort, from all adjusters + carrier)
  const contactChoices: ScalarChoice[] = [];
  adjusters.forEach((adj, adjIdx) => {
    const handles = adj.handles ?? [];
    handles
      .filter((h) => {
        const t = h.handle_type.toUpperCase();
        return t.includes('EMAIL') || t.includes('FAX');
      })
      .forEach((h) => {
        contactChoices.push({
          value: h.value,
          context: {
            coverage_id: coverageId,
            contact_handle: { handle_type: h.handle_type, value: h.value, name: h.name },
            name: adj.name ?? undefined,
            adjuster_id: `adj-${adjIdx}`,
            contact_source: 'adjuster' as const,
          },
        });
      });
  });
  // Also include carrier-level email/fax handles
  const carrierHandles = coverageData.carrier?.handles ?? [];
  carrierHandles
    .filter((h) => {
      const t = h.handle_type.toUpperCase();
      return t.includes('EMAIL') || t.includes('FAX');
    })
    .forEach((h) => {
      contactChoices.push({
        value: h.value,
        context: {
          coverage_id: coverageId,
          contact_handle: { handle_type: h.handle_type, value: h.value, name: h.name },
          carrier: coverageData.carrier ? { name: coverageData.carrier.name } : undefined,
          contact_source: 'carrier' as const,
        },
      });
    });
  // Sort: EMAIL first, FAX second
  const contactRank = (c: ScalarChoice): number => {
    const ctx = c.context as { contact_handle?: { handle_type?: string } } | undefined;
    const t = String(ctx?.contact_handle?.handle_type ?? '').toUpperCase();
    return t.includes('EMAIL') ? 0 : 1;
  };
  contactChoices.sort((a, b) => contactRank(a) - contactRank(b));
  if (contactChoices.length > 0) {
    result[DocumentFieldMapping.ADJUSTER_CONTACT] = contactChoices;
  }

  // Policy number choices
  const policyIds = coverageData.policy_identifiers ?? [];
  const policyChoices: ScalarChoice[] = policyIds
    .filter((p) => p.identifier_value)
    .map((p) => ({
      value: p.identifier_value,
      context: { coverage_id: coverageId, identifier_type: p.identifier_type },
    }));
  if (policyChoices.length > 0) {
    result[DocumentFieldMapping.POLICY_NUMBER] = policyChoices;
  }

  // Claim number choices
  const claims = coverageData.claims ?? [];
  const claimChoices: ScalarChoice[] = claims
    .filter((c) => c.claim_identifier)
    .map((c) => ({
      value: c.claim_identifier as string,
      context: { coverage_id: coverageId, benefit_type: c.benefit_type ?? undefined, claim_identifier: c.claim_identifier ?? undefined },
    }));
  if (claimChoices.length > 0) {
    result[DocumentFieldMapping.CLAIM_NUMBER] = claimChoices;
  }

  // Insured name choices
  const insuredNames = coverageData.named_insured ?? [];
  const insuredChoices: ScalarChoice[] = insuredNames
    .filter(Boolean)
    .map((name) => ({
      value: name,
      context: { coverage_id: coverageId },
    }));
  if (insuredChoices.length > 0) {
    result[DocumentFieldMapping.INSURED_NAME] = insuredChoices;
  }

  return result;
};

// Known LoR field mappings for easy access
export const LOR_FIELD_MAPPINGS = [
  DocumentFieldMapping.ADJUSTER_NAME,
  DocumentFieldMapping.ADJUSTER_CONTACT,
  DocumentFieldMapping.CONTACT_METHOD,
  DocumentFieldMapping.ACCIDENT_LOCATION,
  DocumentFieldMapping.CARRIER_NAME,
  DocumentFieldMapping.CARRIER_ADDRESS_1,
  DocumentFieldMapping.CARRIER_ADDRESS_2,
  DocumentFieldMapping.CARRIER_TELEPHONE,
  DocumentFieldMapping.CARRIER_FAX,
  DocumentFieldMapping.CLAIM_NUMBER,
  DocumentFieldMapping.POLICY_NUMBER,
  DocumentFieldMapping.INSURED_NAME,
  DocumentFieldMapping.CLIENT_NAME,
  DocumentFieldMapping.CLIENT_DATE_OF_BIRTH,
  DocumentFieldMapping.CLIENT_PHONE_NUMBER,
  DocumentFieldMapping.CLIENT_EMAIL_ADDRESS,
  DocumentFieldMapping.DATE_OF_LOSS,
  DocumentFieldMapping.TODAYS_DATE,
  // New mappings supported on LoR page
  DocumentFieldMapping.CMS_FILE_NUMBER,
  DocumentFieldMapping.AT_FAULT_NAME,
  DocumentFieldMapping.AT_FAULT_ADDRESS_1,
  DocumentFieldMapping.AT_FAULT_ADDRESS_2,
  DocumentFieldMapping.PREMISES_NAME,
  DocumentFieldMapping.PREMISES_ADDRESS_1,
  DocumentFieldMapping.PREMISES_ADDRESS_2,
  DocumentFieldMapping.LOSS_TIME,
  DocumentFieldMapping.PLUS_2_HOURS_LOSS_TIME,
  DocumentFieldMapping.MINUS_2_HOURS_LOSS_TIME,
  DocumentFieldMapping.ONE_WEEK_FROM_TODAYS_DATE,
  DocumentFieldMapping.LOSS_FACTS,
  DocumentFieldMapping.CLIENT_ADDRESS_1,
  DocumentFieldMapping.CLIENT_ADDRESS_2,
  DocumentFieldMapping.CLIENT_LAST_NAME,
  DocumentFieldMapping.MANAGING_PARALEGAL_NAME,
  DocumentFieldMapping.MANAGING_PARALEGAL_INITIALS,
  DocumentFieldMapping.MANAGING_PARALEGAL_NUMBER,
  DocumentFieldMapping.MANAGING_PARALEGAL_EMAIL,
] as const;
